"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null);
  return <>
      <div className="container mx-auto px-6 h-24 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/ps travel co logo transparent.png" alt="PS Travel Co" className="h-24 w-auto object-contain" loading="lazy" />
        </Link>

        {}
        <nav className="hidden md:flex items-center gap-10">
          {loading ? <div className="flex gap-8">
              {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-sm" />)}
            </div> : menuItems.map((item, i) => {
          const hasChildren = item.children && item.children.length > 0;
          return <div key={item.id} data-index={i} className="relative group" onMouseEnter={() => setHoveredDropdown(item.id)} onMouseLeave={() => setHoveredDropdown(null)}>
                  <Link to={item.href || "#"} className="flex items-center gap-1 font-mono uppercase text-[12px] tracking-[0.15em] text-foreground py-2 transition-colors hover:text-secondary" aria-haspopup={hasChildren ? "true" : undefined} aria-expanded={hasChildren ? hoveredDropdown === item.id : undefined} onClick={e => {
              if (hasChildren || item.href === "#") {
                e.preventDefault();
                setHoveredDropdown(hoveredDropdown === item.id ? null : item.id);
              }
            }} onKeyDown={e => {
              if (hasChildren && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setHoveredDropdown(hoveredDropdown === item.id ? null : item.id);
              }
            }}>
                    {item.label}
                    {hasChildren && <ChevronDown className={`size-3 transition-transform duration-300 ${hoveredDropdown === item.id ? "rotate-180" : ""}`} />}
                    <span className="absolute -bottom-1 left-1/2 w-0 h-[1px] bg-secondary transition-all duration-300 -translate-x-1/2 group-hover:w-full" />
                  </Link>

                  {}
                  {hasChildren && <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${hoveredDropdown === item.id ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                      <div className="bg-card/95 backdrop-blur-xl border border-border/20 shadow-xl p-4 min-w-[200px] flex flex-col gap-3">
                        {item.children!.map((child, ci) => <Link key={child.id} data-index={ci} to={child.href || "#"} className="font-mono uppercase text-[10px] tracking-[0.1em] text-foreground/80 hover:text-secondary transition-colors whitespace-nowrap">
                            {child.label}
                          </Link>)}
                      </div>
                    </div>}
                </div>;
        })}
        </nav>

        {}
        <div className="md:hidden z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-foreground hover:bg-primary/20">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      <div className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out bg-background/95 backdrop-blur-xl border-b border-border/20 ${isMobileMenuOpen ? "max-h-[80vh] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 border-transparent"}`}>
        <nav className="container mx-auto px-6 py-4 flex flex-col">
          {!loading && menuItems.map((item, i) => {
          const hasChildren = item.children && item.children.length > 0;
          return <div key={item.id} data-index={i} className="flex flex-col">
                  <Link to={item.href || "#"} onClick={e => {
              if (hasChildren) {
                e.preventDefault();
              } else {
                setIsMobileMenuOpen(false);
              }
            }} className="font-mono uppercase text-[12px] tracking-[0.15em] text-foreground py-4 border-b border-border/10 hover:text-secondary transition-colors flex justify-between items-center">
                    {item.label}
                    {hasChildren && <ChevronDown className="size-4 opacity-50" />}
                  </Link>
                  {hasChildren && <div className="pl-4 flex flex-col bg-primary/5">
                      {item.children!.map((child, ci) => <Link key={child.id} data-index={ci} to={child.href || "#"} onClick={() => setIsMobileMenuOpen(false)} className="font-mono uppercase text-[10px] tracking-[0.1em] text-foreground/60 py-3 border-b border-border/5 last:border-0 hover:text-secondary transition-colors">
                          {child.label}
                        </Link>)}
                    </div>}
                </div>;
        })}
        </nav>
      </div>
    </>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="217658" id="sophisticated-navigation" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-700 ${isScrolled ? "bg-background/70 backdrop-blur-[15px] border-b border-border/20 shadow-lg" : "bg-background"}`}>
      <MenuProvider menu_id="18">
        <HeaderContent />
      </MenuProvider>
    </section>;
}